import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, MessageCircle, Send, Loader2, Flag, Trash2 } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CATEGORY_MAP } from "@/lib/categories";
import { getAvatarUrl, initials, formatDate } from "@/lib/helpers";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ReportDialog from "@/components/ReportDialog";

export default function TipCard({ tip, onUpdate, onRemoved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const cat = CATEGORY_MAP[tip.category] || CATEGORY_MAP.general;
  const Icon = cat.icon;
  const [liked, setLiked] = useState(tip.liked_by_me);
  const [likesCount, setLikesCount] = useState(tip.likes_count);
  const [commentsCount, setCommentsCount] = useState(tip.comments_count);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [posting, setPosting] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [removed, setRemoved] = useState(false);

  const toggleLike = async () => {
    // optimistic
    setLiked((v) => !v);
    setLikesCount((c) => c + (liked ? -1 : 1));
    try {
      const { data } = await api.post(`/tips/${tip.id}/like`);
      setLiked(data.liked);
      setLikesCount(data.likes_count);
    } catch {
      // revert
      setLiked((v) => !v);
      setLikesCount((c) => c + (liked ? 1 : -1));
      toast.error("Could not update like");
    }
  };

  const openComments = async () => {
    setShowComments((v) => !v);
    if (!showComments && comments.length === 0) {
      setLoadingComments(true);
      try {
        const { data } = await api.get(`/tips/${tip.id}/comments`);
        setComments(data.comments);
      } catch {}
      setLoadingComments(false);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/tips/${tip.id}/comments`, { text: commentText });
      setComments((c) => [...c, data.comment]);
      setCommentsCount(data.comments_count);
      setCommentText("");
    } catch {
      toast.error("Could not post comment");
    }
    setPosting(false);
  };

  const adminRemove = async () => {
    try {
      await api.delete(`/admin/tips/${tip.id}`);
      toast.success("Tip removed");
      setRemoved(true);
      onRemoved?.(tip.id);
    } catch {
      toast.error("Could not remove");
    }
  };

  if (removed) return null;

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="bg-white rounded-3xl border border-[#1B2A4A]/8 p-5 sm:p-6"
      data-testid={`tip-card-${tip.id}`}
    >
      <div className="flex items-start gap-4">
        <Avatar className="w-11 h-11">
          <AvatarImage src={getAvatarUrl(tip.author?.avatar_path)} />
          <AvatarFallback className="bg-[#2A9D8F] text-white text-xs font-bold">
            {initials(tip.author?.first_name, tip.author?.last_name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-[#1B2A4A] text-[15px]">
              {tip.author?.first_name || "Neighbor"} {tip.author?.last_name || ""}
            </p>
            <span className="text-[#1B2A4A]/30">·</span>
            <p className="text-[13px] text-[#1B2A4A]/60">{formatDate(tip.created_at)}</p>
          </div>
          <div className="mt-1">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-semibold"
              style={{ background: cat.bg, color: cat.color }}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
              {cat.label}
            </span>
          </div>
        </div>
      </div>

      <p className="mt-4 text-[17px] leading-relaxed text-[#1B2A4A]/90">{tip.text}</p>

      <div className="mt-5 flex items-center gap-1 border-t border-[#1B2A4A]/8 pt-3">
        <button
          onClick={toggleLike}
          data-testid={`tip-like-${tip.id}`}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
            liked ? "text-[#E76F51] bg-[#E76F51]/8" : "text-[#1B2A4A]/70 hover:bg-[#1B2A4A]/5"
          }`}
        >
          <Heart className="w-4 h-4" fill={liked ? "currentColor" : "none"} />
          <span>{likesCount}</span>
        </button>
        <button
          onClick={openComments}
          data-testid={`tip-comment-${tip.id}`}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
            showComments ? "text-[#2A9D8F] bg-[#2A9D8F]/8" : "text-[#1B2A4A]/70 hover:bg-[#1B2A4A]/5"
          }`}
        >
          <MessageCircle className="w-4 h-4" />
          <span>{commentsCount}</span>
        </button>
        <div className="flex-1" />
        <button
          onClick={() => setReportOpen(true)}
          data-testid={`tip-report-${tip.id}`}
          title="Report this tip"
          className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium text-[#1B2A4A]/50 hover:text-[#E76F51] hover:bg-[#E76F51]/8 transition-colors duration-200"
        >
          <Flag className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Report</span>
        </button>
        {isAdmin && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid={`tip-admin-remove-${tip.id}`}
                title="Remove tip (admin)"
                className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium text-[#1B2A4A]/50 hover:text-white hover:bg-[#E76F51] transition-colors duration-200"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Remove</span>
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-3xl">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading text-2xl text-[#1B2A4A]">Remove this tip?</AlertDialogTitle>
                <AlertDialogDescription className="text-[#1B2A4A]/70">
                  This will permanently remove the tip and resolve any related reports.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full" data-testid={`tip-admin-remove-cancel-${tip.id}`}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={adminRemove}
                  data-testid={`tip-admin-remove-confirm-${tip.id}`}
                  className="rounded-full bg-[#E76F51] hover:bg-[#E76F51]/90"
                >
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <ReportDialog tipId={tip.id} tipText={tip.text} open={reportOpen} onOpenChange={setReportOpen} />

      <AnimatePresence>
        {showComments && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-4 space-y-3">
              {loadingComments && <Loader2 className="w-4 h-4 animate-spin text-[#1B2A4A]/40" />}
              {comments.map((c) => (
                <div key={c.id} className="flex items-start gap-3 bg-[#F7F5F2] rounded-2xl px-4 py-3">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-[#1B2A4A] text-white text-[10px] font-bold">
                      {initials(c.author_name?.split(" ")[0], c.author_name?.split(" ")[1])}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-[#1B2A4A]">{c.author_name}</p>
                      <p className="text-xs text-[#1B2A4A]/50">{formatDate(c.created_at)}</p>
                    </div>
                    <p className="text-base text-[#1B2A4A]/85 mt-0.5 leading-relaxed">{c.text}</p>
                  </div>
                </div>
              ))}
              <form onSubmit={submitComment} className="flex items-center gap-2 pt-1">
                <Input
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment..."
                  data-testid={`comment-input-${tip.id}`}
                  className="flex-1 h-11 rounded-full border-[#1B2A4A]/15 bg-white focus-visible:ring-2 focus-visible:ring-[#E8621A]/50"
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={posting || !commentText.trim()}
                  data-testid={`comment-submit-${tip.id}`}
                  className="h-11 w-11 rounded-full bg-[#E8621A] hover:bg-[#D05615] transition-colors duration-200"
                >
                  {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}
